// Copyright (c) John Nesky and contributing authors, distributed under the MIT license.
// Shared browser/headless MIDI export. Native score exports retain synthesis exactly;
// MIDI approximates programs, effects and instrument layering.
import {InstrumentType, /*EnvelopeType,*/ Config, getArpeggioPitchIndex} from "../synth/SynthConfig.js";
import {Instrument, Pattern, Note, Song, Synth} from "../synth/synth.js";
import {ArrayBufferWriter} from "./ArrayBufferWriter.js";
import {MidiChunkType, MidiFileFormat, MidiControlEventMessage, MidiEventType, MidiMetaEventMessage, MidiRegisteredParameterNumberMSB, MidiRegisteredParameterNumberLSB, volumeMultToMidiVolume, volumeMultToMidiExpression, defaultMidiPitchBend, defaultMidiExpression} from "./Midi.js";
import {soundPresetCategories, Preset} from "./SoundPresets.js";
const midiChipInstruments: number[] = [
	0x4A, // rounded -> recorder
	0x47, // triangle -> clarinet
	0x50, // square -> square wave
	0x46, // ¹/₄ pulse -> bassoon
	0x44, // ¹/₈ pulse -> oboe
	0x51, // sawtooth -> sawtooth wave
	0x51, // double saw -> sawtooth wave
	0x51, // double pulse -> sawtooth wave
	0x51, // spiky -> sawtooth wave
];

function valueToPreset(value: number): Preset | null { return soundPresetCategories[value >> 6]?.presets[value & 0x3f] || null; }
function lerp(low: number, high: number, t: number): number { return low + t * (high - low); }
export function songToMidi(song: Song, {includeIntro=true, loops=1, includeOutro=true, channelLabel}: {includeIntro?: boolean, loops?: number, includeOutro?: boolean, channelLabel?: (channel: number)=>string} = {}): ArrayBuffer {
 if (!Number.isInteger(loops) || loops < 1 || loops > 4) throw new Error("MIDI loop count must be 1–4.");
	const midiTicksPerBeepBoxTick: number = 2;
	const midiTicksPerBeat: number = midiTicksPerBeepBoxTick * Config.ticksPerPart * Config.partsPerBeat;
	const midiTicksPerPart: number = midiTicksPerBeepBoxTick * Config.ticksPerPart;
	const secondsPerMinute: number = 60;
	const microsecondsPerMinute: number = secondsPerMinute * 1000000;
	const beatsPerMinute: number = song.getBeatsPerMinute();
	const microsecondsPerBeat: number = Math.round(microsecondsPerMinute / beatsPerMinute);
	//const secondsPerMidiTick: number = secondsPerMinute / (midiTicksPerBeat * beatsPerMinute);
	const midiTicksPerBar: number = midiTicksPerBeat * song.beatsPerBar;
	const pitchBendRange: number = 24;
	const defaultNoteVelocity: number = 90;
	
	const unrolledBars: number[] = [];
	if (includeIntro) {
		for (let bar: number = 0; bar < song.loopStart; bar++) {
			unrolledBars.push(bar);
		}
	}
	for (let loopIndex: number = 0; loopIndex < loops; loopIndex++) {
		for (let bar: number = song.loopStart; bar < song.loopStart + song.loopLength; bar++) {
			unrolledBars.push(bar);
		}
	}
	if (includeOutro) {
		for (let bar: number = song.loopStart + song.loopLength; bar < song.barCount; bar++) {
			unrolledBars.push(bar);
		}
	}
	
	const tracks = [{isMeta:  true, channel: -1, midiChannel: -1, isNoise: false, isDrumset: false}];
	let midiChannelCounter: number = 0;
	let foundADrumset: boolean = false;
	for (let channel: number = 0; channel < song.getChannelCount(); channel++) {
		if (!foundADrumset && song.channels[channel].instruments[0].type == InstrumentType.drumset) {
			tracks.push({isMeta: false, channel: channel, midiChannel: 9, isNoise: true, isDrumset: true});
			foundADrumset = true; // There can only be one drumset channel, and it's always channel 9 (seen as 10 in most UIs). :/
		} else {
			if (midiChannelCounter >= 16) continue; // The MIDI standard only supports 16 channels.
			tracks.push({isMeta: false, channel: channel, midiChannel: midiChannelCounter++, isNoise: song.getChannelIsNoise(channel), isDrumset: false});
			if (midiChannelCounter == 9) midiChannelCounter++; // skip midi drum channel.
		}
	}
	
	const writer: ArrayBufferWriter = new ArrayBufferWriter(1024);
	writer.writeUint32(MidiChunkType.header);
	writer.writeUint32(6); // length of headers is 6 bytes
	writer.writeUint16(MidiFileFormat.simultaneousTracks);
	writer.writeUint16(tracks.length);
	writer.writeUint16(midiTicksPerBeat); // number of "ticks" per beat, independent of tempo
	
	for (const track of tracks) {
		writer.writeUint32(MidiChunkType.track);
		
		const {isMeta, channel, midiChannel, isNoise, isDrumset} = track;
		
		// We're gonna come back here and overwrite this placeholder once we know how many bytes this track is.
		const trackStartIndex: number = writer.getWriteIndex();
		writer.writeUint32(0); // placeholder for track size
		
		let prevTime: number = 0;
		let barStartTime: number = 0;
		const writeEventTime = function(time: number): void {
			if (time < prevTime) throw new Error("Midi event time cannot go backwards.");
			writer.writeMidiVariableLength(time - prevTime);
			prevTime = time;
		}
		
		const writeControlEvent = function(message: MidiControlEventMessage, value: number): void {
			if (!(value >= 0 && value <= 0x7F)) throw new Error("Midi control event value out of range: " + value);
			writer.writeUint8(MidiEventType.controlChange | midiChannel);
			writer.writeMidi7Bits(message);
			writer.writeMidi7Bits(value | 0);
		}
		
		if (isMeta) {
			// for first midi track, include tempo, time signature, and key signature information.
			
			writeEventTime(0);
			writer.writeUint8(MidiEventType.meta);
			writer.writeMidi7Bits(MidiMetaEventMessage.text);
			writer.writeMidiAscii("Composed with https://sahildev.com/bop/");
			
			writeEventTime(0);
			writer.writeUint8(MidiEventType.meta);
			writer.writeMidi7Bits(MidiMetaEventMessage.tempo);
			writer.writeMidiVariableLength(3); // Tempo message length is 3 bytes.
			writer.writeUint24(microsecondsPerBeat); // Tempo in microseconds per "quarter" note, commonly known as a "beat"
			
			writeEventTime(0);
			writer.writeUint8(MidiEventType.meta);
			writer.writeMidi7Bits(MidiMetaEventMessage.timeSignature);
			writer.writeMidiVariableLength(4); // Time signature message length is 4 bytes.
			writer.writeUint8(song.beatsPerBar); // numerator.
			writer.writeUint8(2); // denominator exponent in 2^E. 2^2 = 4, and we will always use "quarter" notes.
			writer.writeUint8(24); // MIDI Clocks per metronome tick (should match beats), standard is 24
			writer.writeUint8(8); // number of 1/32 notes per 24 MIDI Clocks, standard is 8, meaning 24 clocks per "quarter" note.
			
			const isMinor: boolean = Config.scales[song.scale].flags[3] && !Config.scales[song.scale].flags[4];
			const key: number = song.key; // C=0, C#=1, counting up to B=11
			let numSharps: number = key; // For even key values in major scale, number of sharps/flats is same...
			if ((key & 1) == 1) numSharps += 6; // For odd key values (consider circle of fifths) rotate around the circle... kinda... Look conventional key signatures are just weird, okay?
			if (isMinor) numSharps += 9; // A minor A scale has zero sharps, shift it appropriately
			while (numSharps > 6) numSharps -= 12; // Range is (modulo 12) - 5. Midi supports -7 to +7, but I only have 12 options.
			
			writeEventTime(0);
			writer.writeUint8(MidiEventType.meta);
			writer.writeMidi7Bits(MidiMetaEventMessage.keySignature);
			writer.writeMidiVariableLength(2); // Key signature message length is 2 bytes.
			writer.writeInt8(numSharps); // See above calculation. Assumes scale is diatonic. :/
			writer.writeUint8(isMinor ? 1 : 0); // 0: major, 1: minor
			
			if (includeIntro) barStartTime += midiTicksPerBar * song.loopStart;
			writeEventTime(barStartTime);
			writer.writeUint8(MidiEventType.meta);
			writer.writeMidi7Bits(MidiMetaEventMessage.marker);
			writer.writeMidiAscii("Loop Start");
			
			for (let loopIndex: number = 0; loopIndex < loops; loopIndex++) {
				barStartTime += midiTicksPerBar * song.loopLength;
				writeEventTime(barStartTime);
				writer.writeUint8(MidiEventType.meta);
				writer.writeMidi7Bits(MidiMetaEventMessage.marker);
				writer.writeMidiAscii(loopIndex < loops - 1 ? "Loop Repeat" : "Loop End");
			}
			
			if (includeOutro) barStartTime += midiTicksPerBar * (song.barCount - song.loopStart - song.loopLength);
			if (barStartTime != midiTicksPerBar * unrolledBars.length) throw new Error("Miscalculated number of bars.");
			
		} else {
			// For remaining tracks, set up the instruments and write the notes:
			
			const channelName: string = song.channels[channel].name || (channelLabel ? channelLabel(channel) : "Channel " + (channel + 1));
			writeEventTime(0);
			writer.writeUint8(MidiEventType.meta);
			writer.writeMidi7Bits(MidiMetaEventMessage.trackName);
			const nameBytes = new TextEncoder().encode(channelName);
                writer.writeMidiVariableLength(nameBytes.length);
                for (const byte of nameBytes) writer.writeUint8(byte);
			
			// This sets up pitch bend range. First we choose the pitch bend RPN (which has MSB and LSB components), then we set the value for that RPN (which also has MSB and LSB components) and finally reset the current RPN to null, which is considered best practice.
			writeEventTime(0); writeControlEvent(MidiControlEventMessage.registeredParameterNumberMSB, MidiRegisteredParameterNumberMSB.pitchBendRange);
			writeEventTime(0); writeControlEvent(MidiControlEventMessage.registeredParameterNumberLSB, MidiRegisteredParameterNumberLSB.pitchBendRange);
			writeEventTime(0); writeControlEvent(MidiControlEventMessage.setParameterMSB, pitchBendRange); // pitch bend semitone range
			writeEventTime(0); writeControlEvent(MidiControlEventMessage.setParameterLSB, 0); // pitch bend cent range
			writeEventTime(0); writeControlEvent(MidiControlEventMessage.registeredParameterNumberMSB, MidiRegisteredParameterNumberMSB.reset);
			writeEventTime(0); writeControlEvent(MidiControlEventMessage.registeredParameterNumberLSB, MidiRegisteredParameterNumberLSB.reset);
			
			let prevInstrumentIndex: number = -1;
			function writeInstrumentSettings(instrumentIndex: number): void {
				const instrument: Instrument = song.channels[channel].instruments[instrumentIndex];
				const preset: Preset | null = valueToPreset(instrument.preset);
				
				if (prevInstrumentIndex != instrumentIndex) {
					prevInstrumentIndex = instrumentIndex;
					writeEventTime(barStartTime);
					writer.writeUint8(MidiEventType.meta);
					writer.writeMidi7Bits(MidiMetaEventMessage.instrumentName);
					writer.writeMidiAscii("Instrument " + (instrumentIndex + 1));
					
					if (!isDrumset) {
						let instrumentProgram: number = 81; // default to sawtooth wave. 
						
						if (preset != null && preset.midiProgram != undefined) {
							instrumentProgram = preset.midiProgram;
						} else if (instrument.type == InstrumentType.drumset) {
							// The first BeepBox drumset channel is handled as a Midi drumset channel and doesn't need a program, but any subsequent drumsets will just be set to taiko.
							instrumentProgram = 116; // taiko
						} else {
							if (instrument.type == InstrumentType.noise || instrument.type == InstrumentType.spectrum) {
								if (isNoise) {
									instrumentProgram = 116; // taiko
								} else {
									instrumentProgram = 75; // pan flute
								}
							} else if (instrument.type == InstrumentType.chip) {
								if (midiChipInstruments.length > instrument.chipWave) {
									instrumentProgram = midiChipInstruments[instrument.chipWave];
								}
							} else if (instrument.type == InstrumentType.pwm || instrument.type == InstrumentType.fm || instrument.type == InstrumentType.harmonics || instrument.type == InstrumentType.supersaw) {
								instrumentProgram = 81; // sawtooth
							} else if (instrument.type == InstrumentType.pickedString) {
								instrumentProgram = 0x19; // steel guitar
							} else {
								throw new Error("Unrecognized instrument type.");
							}
						}
						
						// Program (instrument) change event:
						writeEventTime(barStartTime);
						writer.writeUint8(MidiEventType.programChange | midiChannel);
						writer.writeMidi7Bits(instrumentProgram);
					}
					
					// Instrument volume:
					writeEventTime(barStartTime);
					let instrumentVolume: number = volumeMultToMidiVolume(Synth.instrumentVolumeToVolumeMult(instrument.volume));
					writeControlEvent(MidiControlEventMessage.volumeMSB, Math.min(0x7f, Math.round(instrumentVolume)));
					
					// Instrument pan:
					writeEventTime(barStartTime);
					let instrumentPan: number = (instrument.pan / Config.panCenter - 1) * 0x3f + 0x40;
					writeControlEvent(MidiControlEventMessage.panMSB, Math.min(0x7f, Math.round(instrumentPan)));
				}
			}
			if (song.getPattern(channel, 0) == null) {
				// Go ahead and apply the instrument settings at the beginning of the channel
				// even if a bar doesn't kick in until later.
				writeInstrumentSettings(0);
			}
			
			let prevPitchBend: number = defaultMidiPitchBend;
			let prevExpression: number = defaultMidiExpression;
			let shouldResetExpressionAndPitchBend: boolean = false;
			//let prevTremolo: number = -1;
			const channelRoot: number = isNoise ? Config.spectrumBasePitch : Config.keys[song.key].basePitch;
			const intervalScale: number = isNoise ? Config.noiseInterval : 1;
			
			for (const bar of unrolledBars) {
				const pattern: Pattern | null = song.getPattern(channel, bar);
				
				if (pattern != null) {
					
					const instrumentIndex: number = pattern.instruments[0]; // Don't bother trying to export multiple instruments per pattern to midi, just pick the first one.
					const instrument: Instrument = song.channels[channel].instruments[instrumentIndex];
					const preset: Preset | null = valueToPreset(instrument.preset);
					writeInstrumentSettings(instrumentIndex);
					
					let usesArpeggio: boolean = instrument.getChord().arpeggiates;
					let polyphony: number = usesArpeggio ? 1 : Config.maxChordSize;
					if (instrument.getChord().customInterval) {
						if (instrument.type == InstrumentType.chip || instrument.type == InstrumentType.harmonics) {
							polyphony = 2;
							usesArpeggio = true;
						} else if (instrument.type == InstrumentType.fm) {
							polyphony = Config.operatorCount;
						} else {
							console.error("Unrecognized instrument type for harmonizing arpeggio: " + instrument.type);
						}
					}
					
					for (let noteIndex: number = 0; noteIndex < pattern.notes.length; noteIndex++) {
						const note: Note = pattern.notes[noteIndex];
						
						const noteStartTime: number = barStartTime + note.start * midiTicksPerPart;
						let pinTime: number = noteStartTime;
						let pinSize: number = note.pins[0].size;
						let pinInterval: number = note.pins[0].interval;
						const prevPitches: number[] = [-1, -1, -1, -1];
						const nextPitches: number[] = [-1, -1, -1, -1];
						const toneCount: number = Math.min(polyphony, note.pitches.length);
						const velocity: number = isDrumset ? Math.max(1, Math.round(defaultNoteVelocity * note.pins[0].size / Config.noteSizeMax)) : defaultNoteVelocity;
						
						// The maximum midi pitch bend range is +/- 24 semitones from the base pitch. 
						// To make the most of this, choose a base pitch that is within 24 semitones from as much of the note as possible.
						// This may involve offsetting this base pitch from BeepBox's note pitch.
						let mainInterval: number = note.pickMainInterval();
						let pitchOffset: number = mainInterval * intervalScale;
						if (!isDrumset) {
							let maxPitchOffset: number = pitchBendRange;
							let minPitchOffset: number = -pitchBendRange;
							for (let pinIndex: number = 1; pinIndex < note.pins.length; pinIndex++) {
								const interval = note.pins[pinIndex].interval * intervalScale;
								maxPitchOffset = Math.min(maxPitchOffset, interval + pitchBendRange);
								minPitchOffset = Math.max(minPitchOffset, interval - pitchBendRange);
							}
							/*
							// I'd like to be able to use pitch bend to implement arpeggio, but the "custom inverval" setting in chip instruments combines arpeggio in one tone with another flat tone, and midi can't selectively apply arpeggio to one out of two simultaneous tones. Also it would be hard to reimport. :/
							if (usesArpeggio && note.pitches.length > polyphony) {
								let lowestArpeggioOffset: number = 0;
								let highestArpeggioOffset: number = 0;
								const basePitch: number = note.pitches[toneCount - 1];
								for (let pitchIndex: number = toneCount; pitchIndex < note.pitches.length; pitchIndex++) {
									lowestArpeggioOffset = Math.min(note.pitches[pitchIndex] - basePitch);
									highestArpeggioOffset = Math.max(note.pitches[pitchIndex] - basePitch);
								}
								maxPitchOffset -= lowestArpeggioOffset;
								minPitchOffset += lowestArpeggioOffset;
							}
							*/
							pitchOffset = Math.min(maxPitchOffset, Math.max(minPitchOffset, pitchOffset));
						}
						
						for (let pinIndex: number = 1; pinIndex < note.pins.length; pinIndex++) {
							const nextPinTime: number = noteStartTime + note.pins[pinIndex].time * midiTicksPerPart;
							const nextPinSize: number = note.pins[pinIndex].size;
							const nextPinInterval: number = note.pins[pinIndex].interval;
							
							const length: number = nextPinTime - pinTime;
							for (let midiTick: number = 0; midiTick < length; midiTick++) {
								const midiTickTime: number = pinTime + midiTick;
								const linearSize: number = lerp(pinSize, nextPinSize, midiTick / length);
								const linearInterval: number = lerp(pinInterval, nextPinInterval, midiTick / length);
								
								const interval: number = linearInterval * intervalScale - pitchOffset;
								
								const pitchBend: number = Math.max(0, Math.min(0x3fff, Math.round(0x2000 * (1.0 + interval / pitchBendRange))));
								
								const expression: number = Math.min(0x7f, Math.round(volumeMultToMidiExpression(Synth.noteSizeToVolumeMult(linearSize))));
								
								if (pitchBend != prevPitchBend) {
									writeEventTime(midiTickTime);
									writer.writeUint8(MidiEventType.pitchBend | midiChannel);
									writer.writeMidi7Bits(pitchBend & 0x7f); // least significant bits
									writer.writeMidi7Bits((pitchBend >> 7) & 0x7f); // most significant bits
									prevPitchBend = pitchBend;
								}
								
								if (expression != prevExpression && !isDrumset) {
									writeEventTime(midiTickTime);
									writeControlEvent(MidiControlEventMessage.expressionMSB, expression);
									prevExpression = expression;
								}
								
								const noteStarting: boolean = midiTickTime == noteStartTime;
								for (let toneIndex: number = 0; toneIndex < toneCount; toneIndex++) {
									let nextPitch: number = note.pitches[toneIndex];
									if (isDrumset) {
										nextPitch += mainInterval;
										const drumsetMap: number[] = [
											36, // Bass Drum 1
											41, // Low Floor Tom
											45, // Low Tom
											48, // Hi-Mid Tom
											40, // Electric Snare
											39, // Hand Clap
											59, // Ride Cymbal 2
											49, // Crash Cymbal 1
											46, // Open Hi-Hat
											55, // Splash Cymbal
											69, // Cabasa
											54, // Tambourine
										];
										if (nextPitch < 0 || nextPitch >= drumsetMap.length) throw new Error("Could not find corresponding drumset pitch. " + nextPitch);
										nextPitch = drumsetMap[nextPitch];
									} else {
										if (usesArpeggio && note.pitches.length > toneIndex + 1 && toneIndex == toneCount - 1) {
											const midiTicksSinceBeat = (midiTickTime - barStartTime) % midiTicksPerBeat;
											const midiTicksPerArpeggio = Config.rhythms[song.rhythm].ticksPerArpeggio * midiTicksPerPart / Config.ticksPerPart;
											const arpeggio: number = Math.floor(midiTicksSinceBeat / midiTicksPerArpeggio);
											nextPitch = note.pitches[toneIndex + getArpeggioPitchIndex(note.pitches.length - toneIndex, song.rhythm, arpeggio)];
										}
										nextPitch = channelRoot + nextPitch * intervalScale + pitchOffset;
										if (preset != null && preset.midiSubharmonicOctaves != undefined) {
											nextPitch += 12 * preset.midiSubharmonicOctaves;
										} else if (isNoise) {
											nextPitch += 12 * (+soundPresetCategories.dictionary["Drum Presets"].presets.dictionary["taiko drum"].midiSubharmonicOctaves!);
										}
										
										if (isNoise) nextPitch *= 2;
									}
									nextPitch = Math.max(0, Math.min(127, nextPitch));
									nextPitches[toneIndex] = nextPitch;
									
									if (!noteStarting && prevPitches[toneIndex] != nextPitches[toneIndex]) {
										writeEventTime(midiTickTime);
										writer.writeUint8(MidiEventType.noteOff | midiChannel);
										writer.writeMidi7Bits(prevPitches[toneIndex]); // old pitch
										writer.writeMidi7Bits(velocity); // velocity
									}
								}
								
								for (let toneIndex: number = 0; toneIndex < toneCount; toneIndex++) {
									if (noteStarting || prevPitches[toneIndex] != nextPitches[toneIndex]) {
										writeEventTime(midiTickTime);
										writer.writeUint8(MidiEventType.noteOn | midiChannel);
										writer.writeMidi7Bits(nextPitches[toneIndex]); // new pitch
										writer.writeMidi7Bits(velocity); // velocity
										prevPitches[toneIndex] = nextPitches[toneIndex];
									}
								}
							}
							
							pinTime = nextPinTime;
							pinSize = nextPinSize;
							pinInterval = nextPinInterval;
						}

						const noteEndTime: number = barStartTime + note.end * midiTicksPerPart;
						
						// End all tones.
						for (let toneIndex: number = 0; toneIndex < toneCount; toneIndex++) {
							// TODO: If the note at the start of the next pattern has
							// continuesLastPattern and has the same chord, it ought to extend
							// this previous note.
							writeEventTime(noteEndTime);
							writer.writeUint8(MidiEventType.noteOff | midiChannel);
							writer.writeMidi7Bits(prevPitches[toneIndex]); // pitch
							writer.writeMidi7Bits(velocity); // velocity
						}
						
						shouldResetExpressionAndPitchBend = true;
					}
				} else {
					if (shouldResetExpressionAndPitchBend) {
						shouldResetExpressionAndPitchBend = false;
						
						if (prevExpression != defaultMidiExpression) {
							prevExpression = defaultMidiExpression;
							// Reset expression
							writeEventTime(barStartTime);
							writeControlEvent(MidiControlEventMessage.expressionMSB, prevExpression);
						}
					
						if (prevPitchBend != defaultMidiPitchBend) {
							// Reset pitch bend
							prevPitchBend = defaultMidiPitchBend;
							writeEventTime(barStartTime);
							writer.writeUint8(MidiEventType.pitchBend | midiChannel);
							writer.writeMidi7Bits(prevPitchBend & 0x7f); // least significant bits
							writer.writeMidi7Bits((prevPitchBend >> 7) & 0x7f); // most significant bits
						}
					}
				}
				
				barStartTime += midiTicksPerBar;
			}
		}
		
		writeEventTime(barStartTime);
		writer.writeUint8(MidiEventType.meta);
		writer.writeMidi7Bits(MidiMetaEventMessage.endOfTrack);
		writer.writeMidiVariableLength(0x00);
		
		// Finally, write the length of the track in bytes at the front of the track.
		writer.rewriteUint32(trackStartIndex, writer.getWriteIndex() - trackStartIndex - 4);
	}
	
	return writer.toCompactArrayBuffer();
}
